import { URLS } from "@/base/utils/urls";
import { ModalService } from "@/components/services/modal.service";
import { FieldSet } from "@/components/utils/useFieldSet";
import { yupEmail } from "@/components/yup-utils";
import { cn } from "@/flokit/utils/styling/cn";
import { Description, Title } from "@radix-ui/react-dialog";
import { AxiosError } from "axios";
import * as React from "react";
import * as yup from "yup";
import Buttons from "../../../../button";
import { HStack, VStack } from "../../../../composition/stack.layout";
import { ModalPresentational } from "../../../../modal-shells/modal-presentational";
import { TextView } from "../../../../text-input/text-input";
import { SignInState } from "./sign-in-states";
import { showToast } from "@/flokit/core/toast/show-toast";

export const baseEmailSchema = yup.object({
  email: yupEmail(
    "Please enter a valid email (e.g. your.name@example.com)",
  ).required("This field is required"),
});
export const BaseVerifyEmailSignInModal = (props: {
  onSubmitSignInEmailRequest: () => Promise<void>;
  org: string;
  emailFieldSet: FieldSet<{ email: string }>;
  onEmailChange: (value: string) => void;
  sentMessage?: string;
  redirectPrompt?: React.ReactNode;
  title?: string;
  subtitle?: React.ReactNode;
  redirectUrl?: string;
  emailFieldLabel?: string;
  redirectButtonText?: string;
  center?: boolean;
}) => {
  const [modalState, setModalState] = React.useState(SignInState.SIGN_IN);
  const [isSending, setIsSending] = React.useState(false);

  const submitSignInEmailRequest = async () => {
    props.emailFieldSet.makeAllValidate();
    if (!props.emailFieldSet.hasAllValid) {
      return;
    }

    try {
      setIsSending(true);
      await props.onSubmitSignInEmailRequest();
      setModalState(SignInState.SIGN_IN_SENT);
      setIsSending(false);
    } catch (e: unknown) {
      setIsSending(false);
      const error = e as AxiosError<{ message: string }>;

      if (error.response?.status === 401) {
        showToast({
          type: "error",
          message:
            "You are not in the list of registered agencies for the firm. Please contact the recruiting team.",
        });
      } else {
        showToast({ type: "error", message: "Please enter a valid email." });
      }
      return;
    }
  };

  return (
    <ModalPresentational>
      <VStack className={"gap-y-4 px-8"}>
        {modalState === SignInState.SIGN_IN && (
          <React.Fragment>
            <div
              className={cn(
                "flex flex-col",
                props.center ? "text-center" : "text-left",
              )}
            >
              <Title className="pt-4 font-inter text-4xl font-bold leading-none">
                {props.title ? props.title : "Sign In via Email"}
              </Title>
              <Description
                className={
                  "pb-8 pt-3 text-base leading-none text-dark-elephant"
                }
              >
                {props.subtitle
                  ? props.subtitle
                  : "Please enter your email address below and we will send you a unique sign-in link"}
              </Description>
            </div>

            <HStack className={"w-full justify-center pb-10"}>
              <img
                alt={"email-yay"}
                src={"/v2/email-yay.png"}
                className={"w-40"}
              />
            </HStack>
            <VStack className={"w-full pb-2"}>
              <TextView
                label={props.emailFieldLabel ?? "Email"}
                value={props.emailFieldSet.values.email}
                onChange={props.emailFieldSet.curriedOnChange("email")}
                validation={props.emailFieldSet.validation.email}
                validationMsg={props.emailFieldSet.validationMsg.email}
                onBlur={props.emailFieldSet.onBlur("email")}
                placeholder={"Email Address"}
                required={true}
              />
            </VStack>
            <HStack className={"w-full"}>
              <Buttons.Standard
                className={"w-full"}
                variant={"primary-navy"}
                loading={isSending}
                onClick={() => submitSignInEmailRequest()}
              >
                <span>Submit</span>
              </Buttons.Standard>
            </HStack>
            {props.redirectUrl && (
              <HStack className={"w-full"}>
                <Buttons.LinkStandard
                  className={"w-full"}
                  variant={"ghost"}
                  href={
                    props.redirectUrl
                      ? props.redirectUrl
                      : URLS.agencySelfApplyJobsList(props.org, true)
                  }
                  onClick={() => {
                    ModalService.closeModal();
                  }}
                >
                  <span className="text-navy-sailor">
                    {props.redirectButtonText
                      ? props.redirectButtonText
                      : "Sign in as Agency"}
                  </span>
                </Buttons.LinkStandard>
              </HStack>
            )}
          </React.Fragment>
        )}
        {modalState === SignInState.SIGN_IN_SENT && (
          <SignInLinkSent
            setModalState={(val) => setModalState(val)}
            email={props.emailFieldSet.values.email ?? ""}
            sentMessage={props.sentMessage}
            loading={isSending}
            submit={() => submitSignInEmailRequest()}
          />
        )}
        {modalState === SignInState.RE_ENTER_SIGN_IN && (
          <ReEnterSignIn
            setModalState={(val) => setModalState(val)}
            emailFieldSet={props.emailFieldSet}
            setEmail={(val) => props.onEmailChange(val)}
            loading={isSending}
            submit={() => submitSignInEmailRequest()}
          />
        )}
      </VStack>
    </ModalPresentational>
  );
};

const SignInLinkSent: React.FC<{
  setModalState: (val: SignInState) => void;
  email: string;
  submit: Function;
  loading?: boolean;
  sentMessage?: string;
}> = (props) => {
  return (
    <React.Fragment>
      <Title className="flex justify-center pt-4">
        <span className={"text-4xl"}>Sign-In link sent!</span>
      </Title>
      <Description
        className={
          "flex w-full pb-8 pt-2 text-center text-base text-dark-elephant"
        }
      >
        We have sent this email to {props.email}. <br />
        <br />
        {props.sentMessage
          ? props.sentMessage
          : "Your email will arrive in a few minutes. Check spam if you don't see it in your inbox."}
      </Description>
      <HStack className={"w-full justify-center py-10"}>
        <img alt={"email-yay"} src={"/v2/email-yay.png"} className={"w-40"} />
      </HStack>
      <HStack className={"w-full"}>
        <Buttons.Standard
          className={"w-full"}
          variant={"primary-navy"}
          loading={props.loading}
          onClick={() => props.submit()}
        >
          <span>Resend email</span>
        </Buttons.Standard>
      </HStack>
      <HStack className={"w-full"}>
        <Buttons.Standard
          className={"w-full"}
          variant={"ghost"}
          onClick={() => props.setModalState(SignInState.RE_ENTER_SIGN_IN)}
        >
          <span className="text-navy-sailor">Wrong Email?</span>
        </Buttons.Standard>
      </HStack>
    </React.Fragment>
  );
};

const ReEnterSignIn: React.FC<{
  setModalState: (val: SignInState) => void;
  emailFieldSet: FieldSet<{ email: string }>;
  setEmail: (val: string) => void;
  submit: () => Promise<void>;
  loading?: boolean;
}> = (props) => {
  return (
    <React.Fragment>
      <Title className="flex justify-center pt-4">
        <span className={"text-4xl"}>Re-enter your email</span>
      </Title>
      <Description
        className={"flex pb-8 pt-3 text-center text-base text-dark-elephant"}
      >
        Please enter your email address below and we will send you a unique
        sign-in link.
      </Description>
      <HStack className={"w-full justify-center py-10"}>
        <img alt={"email-yay"} src={"/v2/email-yay.png"} className={"w-40"} />
      </HStack>
      <HStack className={"w-full py-2"}>
        <TextView
          label={"Email"}
          value={props.emailFieldSet.values.email}
          onChange={props.emailFieldSet.curriedOnChange("email")}
          validation={props.emailFieldSet.validation.email}
          validationMsg={props.emailFieldSet.validationMsg.email}
          onBlur={props.emailFieldSet.onBlur("email")}
        />
      </HStack>
      <HStack className={"w-full py-2"}>
        <Buttons.Standard
          className={"w-full"}
          variant={"primary-navy"}
          onClick={() => props.submit()}
          loading={props.loading}
        >
          <span>Submit</span>
        </Buttons.Standard>
      </HStack>
    </React.Fragment>
  );
};
